import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import BackToTop from '@/components/feature/BackToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import Seo from '@/components/base/Seo';
import StanceBar from '@/components/feature/StanceBar';
import { mockTopics } from '@/mocks/topics';
import { categoryName } from '@/components/feature/TopicCard';
import CommentFeed from './components/CommentFeed';

function formatHeat(heat: number) {
  return heat >= 10000 ? `${(heat / 10000).toFixed(1)}万` : `${heat}`;
}

export default function TopicDetail() {
  const { id } = useParams();
  const topic = mockTopics.find((t) => t.id === id);

  const related = topic
    ? [
        ...mockTopics.filter((t) => t.category === topic.category && t.id !== topic.id),
        ...mockTopics.filter((t) => t.category !== topic.category && t.id !== topic.id),
      ].slice(0, 3)
    : [];

  const hotList = [...mockTopics].sort((a, b) => b.heat - a.heat).slice(0, 5);

  if (!topic) {
    return (
      <>
        <Seo
          title="话题不存在 | 网报吃瓜"
          description="该话题可能已被移除或链接有误，返回热议广场查看当前正在讨论的热点。"
          keywords="网报吃瓜,热议话题"
          path="/topics"
        />
        <Navbar />
        <main className="pt-16 md:pt-20 min-h-[70vh] flex items-center justify-center">
          <div className="text-center px-4">
            <span className="mx-auto w-16 h-16 flex items-center justify-center rounded-full bg-background-100 text-foreground-400">
              <i className="ri-emotion-sad-line text-3xl"></i>
            </span>
            <h1 className="mt-5 font-heading text-2xl font-bold text-foreground-950">这个话题还没上桌</h1>
            <p className="mt-3 text-sm text-foreground-600">它可能已经被新的讨论替代，去广场看看现在大家都在围观什么。</p>
            <Link
              to="/topics"
              className="inline-flex mt-6 px-6 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              返回热议广场
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const jsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: topic.title,
      description: topic.summary,
      datePublished: topic.publishedAt,
      inLanguage: 'zh-CN',
      articleSection: categoryName(topic.category),
      keywords: topic.tags.join(','),
      author: { '@type': 'Organization', name: '网报吃瓜' },
      publisher: { '@type': 'Organization', name: '网报吃瓜' },
      mainEntityOfPage: `https://yuanfenq.cn/topic/${topic.id}`,
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: '首页', item: 'https://yuanfenq.cn' },
        { '@type': 'ListItem', position: 2, name: '热议广场', item: 'https://yuanfenq.cn/topics' },
        { '@type': 'ListItem', position: 3, name: topic.title, item: `https://yuanfenq.cn/topic/${topic.id}` },
      ],
    },
  ];

  return (
    <>
      <Seo
        title={`${topic.title} | 网报吃瓜热议`}
        description={`${topic.summary.slice(0, 70)}…网报吃瓜持续跟进该话题的网友评论、讨论走向与不同立场。`}
        keywords={`${topic.tags.join(',')},网报吃瓜,网友热议,讨论看法`}
        path={`/topic/${topic.id}`}
        jsonLd={jsonLd}
      />
      <Navbar />
      <main className="pt-16 md:pt-20">
        <section className="w-full bg-secondary-900 pt-8 pb-10 md:pt-10 md:pb-14">
          <div className="w-full px-4 md:px-10">
            <Breadcrumb
              items={[
                { label: '首页', to: '/' },
                { label: '热议广场', to: '/topics' },
                { label: categoryName(topic.category) },
              ]}
            />
            <div className="mt-5 flex flex-wrap items-center gap-3">
              <span className="px-3 py-1 rounded-md bg-accent-500 text-foreground-950 text-xs font-bold">
                {categoryName(topic.category)}
              </span>
              {topic.tags.map((tag) => (
                <span key={tag} className="px-3 py-1 rounded-md bg-background-50/10 text-background-100 text-xs">
                  #{tag}
                </span>
              ))}
            </div>
            <h1 className="mt-4 font-heading text-2xl md:text-4xl font-black text-background-50 leading-snug max-w-4xl">
              {topic.title}
            </h1>
            <div className="mt-5 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs md:text-sm text-background-200/80">
              <span className="flex items-center gap-1.5">
                <i className="ri-fire-line text-accent-400"></i>热度 {formatHeat(topic.heat)}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-discuss-line"></i>
                {topic.participants.toLocaleString()} 人参与讨论
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-time-line"></i>
                {topic.publishedAt} 更新
              </span>
            </div>
          </div>
        </section>

        <section className="w-full bg-background-50 py-10 md:py-14">
          <div className="w-full px-4 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10">
            <article className="lg:col-span-8">
              <div className="w-full h-56 md:h-80 rounded-lg overflow-hidden">
                <img
                  src={topic.cover}
                  alt={`${topic.title} 讨论场景配图`}
                  className="w-full h-full object-cover object-top"
                />
              </div>

              <h2 className="mt-8 font-heading text-xl md:text-2xl font-bold text-foreground-950">事件概述</h2>
              <p className="mt-4 text-sm md:text-base text-foreground-700 leading-loose">{topic.summary}</p>
              <p className="mt-4 text-sm md:text-base text-foreground-700 leading-loose">
                讨论最初集中在对现象本身的感受，随后逐渐转向具体细节与各自经历。围绕同一件事，网友给出的判断差异不小，
                有人更看重过程中被忽略的环节，也有人认为结果才是有意义的参考。我们把主要观点按立场整理如下，
                方便你先看清大家在争什么，再决定自己的看法。
              </p>

              <h2 className="mt-10 font-heading text-xl md:text-2xl font-bold text-foreground-950">当前立场分布</h2>
              <div className="mt-5 p-5 md:p-6 rounded-lg bg-background-100 border border-background-200/70">
                <StanceBar support={topic.stance.support} oppose={topic.stance.oppose} neutral={topic.stance.neutral} />
                <p className="mt-5 text-sm text-foreground-600 leading-relaxed">
                  说明：立场分布是对近期主要评论的归纳，仅反映讨论规模与情绪倾向，不代表对事件本身的结论。
                </p>
              </div>

              <h2 className="mt-10 font-heading text-xl md:text-2xl font-bold text-foreground-950">讨论时间线</h2>
              <ol className="mt-5 border-l-2 border-background-300/70 ml-2">
                {topic.comments.map((comment, index) => (
                  <li key={`${comment.user}-tl-${index}`} className="relative pl-6 pb-6 last:pb-0">
                    <span className="absolute -left-[7px] top-1.5 w-3 h-3 rounded-full bg-accent-500"></span>
                    <span className="text-xs text-foreground-500">{comment.time}</span>
                    <p className="mt-1 text-sm text-foreground-700">
                      <span className="font-bold text-foreground-900">{comment.user}</span> 发表了一段
                      {comment.stance === 'support' ? '倾向支持' : comment.stance === 'oppose' ? '持保留' : '观望'}的看法
                    </p>
                  </li>
                ))}
              </ol>

              <div className="mt-10 flex items-center justify-between gap-4">
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">网友热议评论</h2>
                <span className="text-xs text-foreground-500">共 {topic.comments.length * 27} 条讨论</span>
              </div>
              <div className="mt-4 border-t border-background-200/70">
                <CommentFeed comments={topic.comments} />
              </div>
            </article>

            <aside className="lg:col-span-4">
              <div className="lg:sticky lg:top-24 flex flex-col gap-6">
                <div className="p-5 md:p-6 rounded-lg bg-accent-50 border border-accent-200/70">
                  <h3 className="font-heading text-base font-bold text-foreground-950">话题速览</h3>
                  <dl className="mt-4 space-y-3 text-sm">
                    <div className="flex items-center justify-between">
                      <dt className="text-foreground-600">热度值</dt>
                      <dd className="font-bold text-foreground-900">{formatHeat(topic.heat)}</dd>
                    </div>
                    <div className="flex items-center justify-between">
                      <dt className="text-foreground-600">参与人数</dt>
                      <dd className="font-bold text-foreground-900">{topic.participants.toLocaleString()}</dd>
                    </div>
                    <div className="flex items-center justify-between">
                      <dt className="text-foreground-600">所属分区</dt>
                      <dd className="font-bold text-foreground-900">{categoryName(topic.category)}</dd>
                    </div>
                    <div className="flex items-center justify-between">
                      <dt className="text-foreground-600">最近更新</dt>
                      <dd className="font-bold text-foreground-900">{topic.publishedAt.split(' ')[0]}</dd>
                    </div>
                  </dl>
                </div>

                <div className="p-5 md:p-6 rounded-lg bg-background-100 border border-background-200/70">
                  <h3 className="font-heading text-base font-bold text-foreground-950">全网温度榜</h3>
                  <ol className="mt-4 space-y-3">
                    {hotList.map((item, index) => (
                      <li key={item.id}>
                        <Link
                          to={`/topic/${item.id}`}
                          className="flex items-start gap-3 text-sm cursor-pointer group"
                        >
                          <span
                            className={`w-5 shrink-0 font-heading font-bold ${
                              index < 3 ? 'text-primary-500' : 'text-foreground-400'
                            }`}
                          >
                            {index + 1}
                          </span>
                          <span className="text-foreground-700 group-hover:text-primary-600 transition-colors line-clamp-2">
                            {item.title}
                          </span>
                        </Link>
                      </li>
                    ))}
                  </ol>
                </div>

                <div className="p-5 md:p-6 rounded-lg bg-secondary-50 border border-secondary-200/70">
                  <h3 className="font-heading text-base font-bold text-foreground-950">相关话题</h3>
                  <ul className="mt-4 space-y-3">
                    {related.map((item) => (
                      <li key={item.id}>
                        <Link to={`/topic/${item.id}`} className="block cursor-pointer group">
                          <span className="block text-xs text-secondary-700 mb-1">{categoryName(item.category)}</span>
                          <span className="block text-sm text-foreground-700 group-hover:text-secondary-700 transition-colors line-clamp-2">
                            {item.title}
                          </span>
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </aside>
          </div>
        </section>

        <section className="w-full bg-background-100 py-12 md:py-16">
          <div className="w-full px-4 md:px-10">
            <div className="flex items-end justify-between gap-4 mb-6 md:mb-8">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">你可能也在围观</h2>
              <Link
                to="/topics"
                className="text-sm font-medium text-primary-600 hover:text-primary-700 cursor-pointer whitespace-nowrap"
              >
                更多热议 <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {related.map((item) => (
                <Link
                  key={item.id}
                  to={`/topic/${item.id}`}
                  className="group flex gap-4 p-4 rounded-lg bg-background-50 border border-background-200/80 hover:border-primary-300 transition-colors cursor-pointer"
                >
                  <span className="w-20 h-20 shrink-0 rounded-md overflow-hidden">
                    <img
                      src={item.cover}
                      alt={`${item.title} 缩略配图`}
                      className="w-full h-full object-cover object-top"
                    />
                  </span>
                  <span className="min-w-0">
                    <span className="block text-xs text-primary-600 mb-1">{categoryName(item.category)}</span>
                    <span className="block font-heading text-sm font-bold text-foreground-950 leading-snug line-clamp-2 group-hover:text-primary-600 transition-colors">
                      {item.title}
                    </span>
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <BackToTop />
    </>
  );
}